import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { SmallTableEmptyState } from '@/components/display/SmallTableEmptyState/SmallTableEmptyState';
import { Button } from '@/components/form/baseInputs/Button';
import { FileSearch02Icon } from '@/components/icons/FileSearch02Icon';
import { Upload01Icon } from '@/components/icons/Upload01Icon';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { Loader } from '@/components/progress/Loader/Loader';
import { DataTableCard } from '@/components/tables/DataTable/DataTableCard';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { useClientDocumentsPageContext } from '@/modules/documents/contexts/clientDocumentsPage.context';
import { GenerateMemoSection } from '@/modules/memos/components/GenerateMemoSection';
import { MemoGeneratingCallout } from '@/modules/memos/components/MemoGeneratingCallout';
import { CreateMemoModal } from '@/modules/memos/CreateMemoModal/CreateMemoModal';
import {
  CreatableMemoPromptType,
  MEMOS_EMPTY_STATE_DESCRIPTION,
} from '@/modules/memos/memos.constants';
import { MemosTable } from '@/modules/memos/MemosTable/MemosTable';
import { ROUTE_KEYS } from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';
import { COLORS } from '@/styles/tokens/colors';
import { getNodes } from '@/utils/graphqlUtils';

import { useMemosTabQuery } from './graphql/MemosTab.generated';
import { getMemosTabViewState, MemosTabViewState } from './MemosTab.utils';

interface MemosTabProps {
  householdId: string;
}

export function MemosTab({ householdId }: MemosTabProps) {
  const navigate = useNavigate();
  const { createErrorFeedback } = useFeedback();
  const canEditHouseholdData = useCanEditHouseholdData();
  const { setModalOpen } = useClientDocumentsPageContext();
  const [selectedMemoPromptType, setSelectedMemoPromptType] =
    useState<CreatableMemoPromptType | null>(null);

  const handleUploadDocuments = useCallback(() => {
    setModalOpen(true);
    navigate(
      getCompletePathFromRouteKey(ROUTE_KEYS.HOUSEHOLD_DETAILS_DOCUMENTS, {
        householdId,
      }),
      { replace: true }
    );
  }, [householdId, navigate, setModalOpen]);

  const { data, loading } = useMemosTabQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      householdId,
    },
    onError: createErrorFeedback(
      `We couldn't load documents. Please refresh and try again.`
    ),
  });

  const hasDocuments = getNodes(data?.documents).length > 0;
  const viewState = getMemosTabViewState({
    hasDocuments,
    isInitialLoading: loading && data === undefined,
  });

  return (
    <>
      <MemoGeneratingCallout />
      {canEditHouseholdData && hasDocuments && (
        <GenerateMemoSection
          onSelectPromptType={(memoPromptType) => {
            setSelectedMemoPromptType(memoPromptType);
          }}
        />
      )}
      <DataTableCard
        sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
      >
        {viewState === MemosTabViewState.Loading && (
          <Loader
            boxProps={{
              sx: {
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
                py: 22,
              },
            }}
            circularProgressProps={{ size: 75 }}
          />
        )}
        {viewState === MemosTabViewState.DocumentsEmpty && (
          <SmallTableEmptyState
            fill="flex"
            icon={<FileSearch02Icon size={32} color={COLORS.TEXT.PRIMARY} />}
            heading="No memos"
            description={MEMOS_EMPTY_STATE_DESCRIPTION}
            action={
              canEditHouseholdData ? (
                <Button
                  variant="primary"
                  size="sm"
                  startIcon={Upload01Icon}
                  onClick={handleUploadDocuments}
                >
                  Upload documents
                </Button>
              ) : undefined
            }
          />
        )}
        {viewState === MemosTabViewState.Table && (
          <MemosTable householdId={householdId} />
        )}
      </DataTableCard>
      {canEditHouseholdData && selectedMemoPromptType && (
        <CreateMemoModal
          isOpen
          householdId={householdId}
          memoPromptType={selectedMemoPromptType}
          onClose={() => {
            setSelectedMemoPromptType(null);
          }}
        />
      )}
    </>
  );
}
